import { useCallback, useMemo, useState } from 'react';
import { useToggle } from '@sdk/common/hooks/useToggle';

import { useGetAnnouncements } from '@web/announcements/hooks/useGetAnnouncements';

export const useAnnouncementItems = () => {
  const { data, isFetched, isLoading } = useGetAnnouncements();

  const items = data?.pages.flatMap((item) => item).filter((item) => !item.isDeleted) || [];

  const itemsCount = items.length;
  const hasEmptyData = isFetched && itemsCount === 0;

  return {
    hasEmptyData,
    items,
    itemsCount,
    isLoading,
  };
};

export const useSelectId = () => {
  const [selectedId, setSelectedId] = useState<number | null>(null);

  return useMemo(
    () => ({
      unselect: () => setSelectedId(null),
      isSelected: !!selectedId,
      select: (id: number) => setSelectedId(id),
      selectedId,
    }),
    [selectedId]
  );
};

export const useBulkSelectId = () => {
  const { isOn, setOff, setOn } = useToggle(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const toggleSelection = useCallback(
    (id: number, removeOnly = false) => {
      const isSelected = selectedIds.has(id);

      if (removeOnly && !isSelected) {
        return;
      }

      isSelected ? selectedIds.delete(id) : selectedIds.add(id);

      setSelectedIds(new Set(selectedIds));
    },
    [selectedIds]
  );

  const clearSelection = (preserveIds?: number[]) => {
    setSelectedIds(new Set(preserveIds));
  };

  return {
    cancelBulkSelection: setOff,
    clearSelection,
    confirmBulkSelection: setOn,
    hasSelection: selectedIds.size > 0,
    isBulkSelectionConfirmed: isOn,
    selectedIds,
    toggleSelection,
  };
};
